/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardNavbar from '../../components/dashboard/DashboardNavbar';
import PromotionService from '../../services/promotion.service';
import CreatePromotionForm from '../../components/promotions/CreatePromotionForm';
import PromotionPreview from '../../components/promotions/PromotionPreview';
import { PathRoutes } from '../../components/routes/PathRoutes';
import { IPromotion } from '../../domain/interfaces/Promotions';

const PromotionsManagement: React.FC = () => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'list' | 'create'>('list');
  const [promotions, setPromotions] = useState<IPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState({
    title: '',
    description: '',
    discount: 0,
    imageUrl: ''
  });

  // Obtener ID de la empresa
  const getCompanyId = (): string | null => {
    // Intentar obtener el companyId de diferentes fuentes
    if (userInfo) {
      // Verificar si existe companyId directamente
      if (userInfo.companyId) {
        return userInfo.companyId;
      }
      // Verificar si existe como propiedad anidada
      if ((userInfo as any).company?.id) {
        return (userInfo as any).company.id;
      }
      // Verificar si el userInfo.id es el companyId (para usuarios tipo empresa)
      if (userInfo.id && userInfo.role === 'company') {
        return userInfo.id;
      }
    }

    // Como fallback, intentar obtener de localStorage
    const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId');
    if (companyId) {
      return companyId;
    }

    return null;
  };

  // Cargar promociones
  const fetchPromotions = async () => {
    const companyId = getCompanyId();
    if (!companyId) {
      setError('No se pudo obtener el ID de la empresa');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const promotionsData = await PromotionService.getCompanyPromotions(companyId);
      setPromotions(promotionsData);
      console.log("promociones obtenidas de la empresa", promotionsData)
      setError(null);
    } catch (err) {
      console.error('Error al cargar promociones:', err);
      setError('Error al cargar las promociones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, [userInfo]);

  // Cambiar estado de promoción
  const handleTogglePromotion = async (promotionId: string, isCurrentlyActive: boolean) => {
    try {
      setError(null); // Limpiar errores previos

      // Si se va a activar una promoción (cambiar de inactiva a activa)
      if (!isCurrentlyActive) {
        // Activar la promoción seleccionada
        await PromotionService.activatePromotion(promotionId);
      } else {
        // Desactivar la promoción
        await PromotionService.deactivatePromotion(promotionId);
      }

      // Recargar promociones para reflejar los cambios
      await fetchPromotions();
    } catch (err: any) {
      console.error('Error al cambiar estado de promoción:', err);

      // Mostrar el mensaje de error específico del servicio
      const errorMessage = err.message || 'Error al cambiar el estado de la promoción';
      setError(errorMessage);
    }
  };

  // Función para obtener el color del badge de estado
  const getEstadoBadgeColor = (estado: number) => {
    switch (estado) {
      case 0:
        return 'bg-red-100 text-red-800'; // Inactiva
      case 1:
        return 'bg-green-100 text-green-800'; // Activa
      case 2:
        return 'bg-gray-100 text-gray-800'; // Expirada
      case 3:
        return 'bg-yellow-100 text-yellow-800'; // Suspendida
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Función para obtener el texto del estado
  const getEstadoTexto = (estado: number) => {
    switch (estado) {
      case 0:
        return 'Inactiva';
      case 1:
        return 'Activa';
      case 2:
        return 'Expirada';
      case 3:
        return 'Suspendida';
      default:
        return 'Desconocido';
    }
  };


  return (
    <div className="min-h-screen bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark">
      <DashboardNavbar />

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-xervix-text-primary dark:text-xervix-text-primary-dark">Gestión de Promociones</h1>
            <p className="text-xervix-text-secondary dark:text-xervix-text-secondary-dark">Administra las promociones de tu empresa</p>
          </div>
          <button
            onClick={() => navigate(PathRoutes.dashboard)}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Volver al Dashboard
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('list')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${activeTab === 'list'
                  ? 'border-xervix-primary text-xervix-primary'
                  : 'border-transparent text-xervix-text-secondary hover:text-xervix-text-primary hover:border-gray-300'
                  }`}
              >
                Mis Promociones
              </button>
              <button
                onClick={() => setActiveTab('create')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${activeTab === 'create'
                  ? 'border-xervix-primary text-xervix-primary'
                  : 'border-transparent text-xervix-text-secondary hover:text-xervix-text-primary hover:border-gray-300'
                  }`}
              >
                Crear Promoción
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'list' && (
              <div>
                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-xervix-primary mx-auto"></div>
                    <p className="mt-4 text-xervix-text-secondary">Cargando promociones...</p>
                  </div>
                ) : promotions.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-6xl mb-4">📢</div>
                    <h3 className="text-lg font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
                      No tienes promociones
                    </h3>
                    <p className="text-xervix-text-secondary dark:text-xervix-text-secondary-dark mb-4">
                      Crea tu primera promoción para atraer más clientes
                    </p>
                    <button
                      onClick={() => setActiveTab('create')}
                      className="bg-xervix-primary hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Crear Promoción
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-xervix-text-secondary dark:text-xervix-text-secondary-dark uppercase tracking-wider">
                            Promoción
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-xervix-text-secondary dark:text-xervix-text-secondary-dark uppercase tracking-wider">
                            Descuento
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-xervix-text-secondary dark:text-xervix-text-secondary-dark uppercase tracking-wider">
                            Inicio
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-xervix-text-secondary dark:text-xervix-text-secondary-dark uppercase tracking-wider">
                            Fin
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-xervix-text-secondary dark:text-xervix-text-secondary-dark uppercase tracking-wider">
                            Estado
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-xervix-text-secondary dark:text-xervix-text-secondary-dark uppercase tracking-wider">
                            Activa
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark divide-y divide-gray-200 dark:divide-gray-700">
                        {promotions.map((promotion) => (
                          <tr key={promotion.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-16 w-16">
                                  <img
                                    className="h-16 w-16 rounded-lg object-cover"
                                    src={promotion.imageUrl || 'https://via.placeholder.com/64x64?text=IMG'}
                                    alt={promotion.title}
                                  />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark">
                                    {promotion.title}
                                  </div>
                                  <div className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark max-w-xs truncate">
                                    {promotion.description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-2xl font-bold text-xervix-primary">
                                {promotion.discount}% OFF
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark">
                              {new Date(promotion.startDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark">
                              {new Date(promotion.endDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadgeColor(promotion.status)}`}>
                                {getEstadoTexto(promotion.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={promotion.status === 1}
                                  onChange={() => handleTogglePromotion(promotion.id, promotion.status === 1)}
                                  disabled={promotion.status === 2 || promotion.status === 3} // Deshabilitado si está expirada o suspendida
                                  className="sr-only"
                                />
                                <div className={`relative w-10 h-6 transition duration-200 ease-linear rounded-full ${promotion.status === 1 ? 'bg-green-500' : 'bg-gray-300'
                                  } ${(promotion.status === 2 || promotion.status === 3) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                  <div className={`absolute left-0 top-0 bg-white w-6 h-6 rounded-full transition transform duration-100 ease-linear ${promotion.status === 1 ? 'translate-x-full' : ''
                                    }`}></div>
                                </div>
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'create' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Formulario de creación */}
                <div>
                  <CreatePromotionForm
                    companyId={getCompanyId() || ''}
                    onPromotionCreated={() => {
                      fetchPromotions();
                      setActiveTab('list');
                    }}
                    onPreviewDataChange={setPreviewData}
                  />
                </div>

                {/* Preview de la promoción */}
                <div>
                  <PromotionPreview
                    title={previewData.title}
                    description={previewData.description}
                    discount={previewData.discount}
                    imageUrl={previewData.imageUrl}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionsManagement;
